
import { toast } from 'sonner';
import { processStripePayment } from './stripeService';

// Function to send SMS verification
export const sendSmsVerification = async (
  phoneNumber: string, 
  message: string
): Promise<boolean> => {
  try {
    console.log(`Sending SMS to ${phoneNumber}: ${message}`);
    // This is a mock implementation - in a real app you would use Twilio or similar
    await new Promise(resolve => setTimeout(resolve, 500));
    return true;
  } catch (error) {
    console.error('SMS sending error:', error);
    toast.error('Failed to send SMS verification');
    return false;
  }
};

// Function to verify SMS code
export const verifySmsCode = async (
  phoneNumber: string, 
  code: string
): Promise<boolean> => {
  try {
    console.log(`Verifying SMS code for ${phoneNumber}: ${code}`);
    // This is a mock implementation - in a real app you would verify with your SMS provider
    const isValid = code === '1234' || code === '123456'; // Example valid codes
    return isValid;
  } catch (error) {
    console.error('SMS verification error:', error);
    toast.error('Failed to verify SMS code');
    return false;
  }
};

// Function to process payment via SMS
export const processSmsPayment = async (
  phoneNumber: string,
  amount: number
): Promise<{
  success: boolean;
  transactionId?: string;
  error?: string;
}> => {
  try {
    console.log(`Processing SMS payment of $${amount} to ${phoneNumber}`);
    
    // In a real implementation, this would likely involve several steps:
    // 1. Verify the phone number exists in your system
    // 2. Send a payment authorization SMS
    // 3. Verify the authorization
    // 4. Process the actual payment
    
    // This example simply delegates to Stripe for demo purposes
    const recipientId = `phone:${phoneNumber}`;
    const result = await processStripePayment(amount, recipientId);
    
    if (!result.success) {
      throw new Error(result.error || 'Payment processing failed');
    }
    
    return {
      success: true,
      transactionId: result.transactionId
    };
  } catch (error) {
    console.error('SMS payment error:', error);
    toast.error('SMS payment failed');
    return {
      success: false,
      error: error.message || 'An unexpected error occurred'
    };
  }
};

// NEW: Function to send payment by phone number (requested by QuickSmsTransfer component)
export const sendPaymentByPhone = async (
  phoneNumber: string,
  amount: number
): Promise<boolean> => {
  try {
    console.log(`Sending payment of $${amount} to ${phoneNumber}`);
    toast.info('Processing SMS payment...');
    
    // Mock AI processing and verification
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Simulate sending an SMS with payment instructions
    const smsResult = await sendSmsVerification(
      phoneNumber, 
      `You've received $${amount.toFixed(2)}! Claim at bluetoothcash.net. Verification: BTC-${Date.now().toString().slice(-6)}`
    );
    
    if (!smsResult) {
      toast.error('Could not send payment SMS');
      return false;
    }
    
    // Process actual payment (using Stripe backend)
    const paymentResult = await processSmsPayment(phoneNumber, amount);
    
    return paymentResult.success;
  } catch (error) {
    console.error('Payment by phone error:', error);
    toast.error('Failed to process phone payment');
    return false;
  }
};

// NEW: Function to process unverified SMS transfer with AI risk assessment
export const processUnverifiedSmsTransfer = async (
  phoneNumber: string,
  amount: number
): Promise<boolean> => {
  try {
    console.log(`Processing unverified transfer of $${amount} to ${phoneNumber}`);
    
    // Simulate AI risk assessment process
    toast.info('Running AI risk assessment...');
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Generate a random risk score (1-100)
    const riskScore = Math.floor(Math.random() * 100) + 1;
    console.log(`AI risk assessment score: ${riskScore}/100`);
    
    // High risk transfers are rejected
    if (riskScore > 80) {
      toast.error('Transfer rejected: High risk assessment score');
      return false;
    }
    
    // Send SMS notification (without verification)
    const notificationSent = await sendSmsVerification(
      phoneNumber,
      `You've received $${amount.toFixed(2)}! Claim at bluetoothcash.net with code: ${Date.now().toString().slice(-6)}`
    );
    
    if (!notificationSent) {
      toast.error('Could not send notification SMS');
      return false;
    }
    
    // Process the payment with flagged status
    const recipientId = `unverified:${phoneNumber}`;
    const result = await processStripePayment(amount, recipientId);
    
    return result.success;
  } catch (error) {
    console.error('Unverified SMS transfer error:', error);
    toast.error('Unverified transfer failed');
    return false;
  }
};

// Export these functions together
export const smsService = {
  sendVerification: sendSmsVerification,
  verifyCode: verifySmsCode,
  processPayment: processSmsPayment,
  sendPaymentByPhone, // Add new function to the service object
  processUnverifiedSmsTransfer // Add new function to the service object
};
